/**
 * Tiny TTL cache backed by sessionStorage when available (so data survives
 * page navigations within a session), with an in-memory fallback for SSR,
 * private browsing, or quota errors. All entries carry an absolute expiry.
 */

export const DEFAULT_TTL_MS = 5 * 60 * 1000

interface Entry {
  v: unknown
  /** epoch ms */
  e: number
}

const memory = new Map<string, Entry>()

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null
    const probe = '__stoq_sdk_probe__'
    window.sessionStorage.setItem(probe, '1')
    window.sessionStorage.removeItem(probe)
    return window.sessionStorage
  } catch {
    return null
  }
}

export function cacheGet<T>(key: string): T | null {
  const now = Date.now()
  const store = storage()
  if (store) {
    try {
      const raw = store.getItem(key)
      if (raw === null) return null
      const entry = JSON.parse(raw) as Entry
      if (typeof entry?.e !== 'number' || entry.e <= now) {
        store.removeItem(key)
        return null
      }
      return entry.v as T
    } catch {
      return null
    }
  }

  const entry = memory.get(key)
  if (!entry) return null
  if (entry.e <= now) {
    memory.delete(key)
    return null
  }
  return entry.v as T
}

export function cacheSet(key: string, value: unknown, ttlMs: number = DEFAULT_TTL_MS): void {
  const entry: Entry = { v: value, e: Date.now() + ttlMs }
  const store = storage()
  if (store) {
    try {
      store.setItem(key, JSON.stringify(entry))
      return
    } catch {
      // Quota/serialization failure — fall through to memory.
    }
  }
  memory.set(key, entry)
}

export function cacheRemove(key: string): void {
  const store = storage()
  if (store) {
    try {
      store.removeItem(key)
    } catch {
      // ignore
    }
  }
  memory.delete(key)
}

/** Test helper: wipes the in-memory fallback store. */
export function clearMemoryCache(): void {
  memory.clear()
}
